import { useMemo } from "react"
import { Chart } from "react-chartjs-2"
import { EmptyState, ErrorState } from "procurement/design"
import { useGetBudgetDashboardTrendQuery } from "shared/redux/endpoints/procurementV2"
import { SERIES, baseOptions, gridColor, moneyTick, tickColor } from "./chartSetup"

const line = (label, data, color, extra = {}) => ({
  type: "line",
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  pointRadius: 3,
  tension: 0.25,
  yAxisID: "ytd",
  ...extra,
})

const SpendingTrendChart = ({ params }) => {
  const { data, isFetching, isError } = useGetBudgetDashboardTrendQuery({ ...params, size: 12 })
  const rows = useMemo(
    () =>
      [...(data?.items || [])].sort((a, b) => a.fiscalYear - b.fiscalYear || a.fiscalMonth - b.fiscalMonth),
    [data]
  )

  const chart = useMemo(
    () => ({
      labels: rows.map((r) => `${String(r.fiscalMonth).padStart(2, "0")}/${r.fiscalYear}`),
      datasets: [
        line("Planned budget (cumulative)", rows.map((r) => r.budgetYtd), SERIES.budget, {
          borderDash: [6, 4],
          pointRadius: 0,
        }),
        line("Used (cumulative)", rows.map((r) => r.usedYtd), SERIES.used, {
          fill: true,
          backgroundColor: "rgba(79, 90, 224, 0.12)",
        }),
        line("Paid (cumulative)", rows.map((r) => r.paidYtd), SERIES.paid),
        line("Committed (cumulative)", rows.map((r) => r.committedYtd), SERIES.committed),
        {
          type: "bar",
          label: "Used in the month",
          data: rows.map((r) => r.usedMonthChange),
          backgroundColor: "rgba(29, 78, 216, 0.35)",
          yAxisID: "month",
          barPercentage: 0.5,
        },
      ],
    }),
    [rows]
  )

  const options = useMemo(
    () => ({
      ...baseOptions,
      scales: {
        x: { grid: { display: false }, ticks: { color: tickColor } },
        ytd: { position: "left", ticks: { callback: moneyTick, color: tickColor }, grid: { color: gridColor } },
        month: { position: "right", ticks: { callback: moneyTick, color: tickColor }, grid: { display: false }, beginAtZero: true },
      },
    }),
    []
  )

  if (isError) return <ErrorState text="Couldn't load the spending trend." />
  if (!isFetching && rows.length === 0) return <EmptyState title="No trend data for this period." />

  return (
    <>
      <div className="br-chart" style={{ height: 320 }}>
        <Chart type="bar" data={chart} options={options} />
      </div>
      <p className="pc-hint br-note">
        <strong>Cumulative</strong> (year-to-date) = total from the first month of the fiscal year up to that month.
        Dashed line = planned budget; lines = actual spending; bars (right axis) = amount used in that month alone.
      </p>
    </>
  )
}

export default SpendingTrendChart
